import {Component, effect, signal, computed, model, viewChild, ElementRef, ChangeDetectionStrategy, inject, AfterViewInit, OnDestroy} from "@angular/core"

import {toObservable} from "@angular/core/rxjs-interop"
import {BaseInspectorComponent} from "@template-editor/components/inspectors/base-inspector/base-inspector.component"
import {InspectorSectionComponent} from "@template-editor/components/inspectors/inspector-section/inspector-section.component"
import {ValueSlotComponent} from "@template-editor/components/value-slot/value-slot.component"
import {ButtonComponent} from "@app/common/components/buttons/button/button.component"
import {PlanarCurve, PlanarCurveControlPoint, getSectionIdForControlPointIdxMap, ParametricCurve, InfoNodes} from "@cm/template-nodes"
import {Vector2Like, Vector2, Matrix3x2} from "@cm/math"
import {Three as THREE} from "@cm/material-nodes/three"
import {Subject, take} from "rxjs"
import {z} from "zod/v4-mini"
import {SplineCurveComponent} from "@app/common/components/curves/spline-curve/spline-curve.component"

function toSplineCoordinates(point: Vector2Like, scale: number, offset: Vector2) {
    return {x: (point.x - offset.x) / scale + 0.5, y: (point.y - offset.y) / scale + 0.5}
}

function fromSplineCoordinates(point: Vector2Like, scale: number, offset: Vector2) {
    return new Vector2((point.x - 0.5) * scale + offset.x, (point.y - 0.5) * scale + offset.y)
}

function splineToPixelCoordinates(point: Vector2Like, width: number, height: number) {
    return {x: point.x * width, y: (1 - point.y) * height}
}

function colorForSectionId(sectionId: number, sectionsCount: number) {
    if (sectionsCount === 1) return "black"

    const hue = (sectionId / sectionsCount) * 360
    return `hsl(${hue}, 100%, 50%)`
}

function smoothPathPoint(newPoint: Vector2, points: Vector2[], smoothingFactor: number) {
    const smoothedPoints = [...points]
    if (smoothedPoints.length === 0) return newPoint

    const lastPoint = smoothedPoints[smoothedPoints.length - 1]
    const newSmoothedPoint = newPoint.lerp(lastPoint, smoothingFactor)

    return newSmoothedPoint
}

function rotatePoints(points: Vector2[], angle: number) {
    const angleInRadians = (angle * Math.PI) / 180
    const rotationMatrix = new Matrix3x2([Math.cos(angleInRadians), -Math.sin(angleInRadians), Math.sin(angleInRadians), Math.cos(angleInRadians), 0, 0])
    return points.map((point) => rotationMatrix.multiplyVector(point))
}

function controlPointsFromDrawingPoints(initialDrawingPoints: Vector2[], currentScale: number) {
    type ControlPoint = {drawingPointIndex: number}

    const getInitalControlPoints = () => {
        const controlPoints: ControlPoint[] = [{drawingPointIndex: 0}, {drawingPointIndex: initialDrawingPoints.length - 1}]

        const threshold = currentScale / 50
        const maxIters = initialDrawingPoints.length - 2

        const getMaxDistanceDrawingPointInSection = (sectionStartIndex: number, sectionEndIndex: number) => {
            if (sectionEndIndex - sectionStartIndex < 1) return undefined

            const sectionStart = initialDrawingPoints[sectionStartIndex]
            const sectionEnd = initialDrawingPoints[sectionEndIndex]
            const base0 = sectionEnd.sub(sectionStart).normalized()
            const base1 = new Vector2(-base0.y, base0.x)
            const transform = new Matrix3x2([base0.x, base0.y, base1.x, base1.y, sectionStart.x, sectionStart.y])
            const inverse = transform.inverse()

            const getDistanceForPointIndex = (pointIndex: number) => {
                const point = inverse.multiplyVector(initialDrawingPoints[pointIndex])
                return Math.abs(point.y)
            }

            return initialDrawingPoints.slice(sectionStartIndex + 2, sectionEndIndex).reduce(
                (maxDistancePoint, currentPoint, currentSectionIndex) => {
                    const currentIndex = currentSectionIndex + sectionStartIndex + 2
                    const pointDistance = getDistanceForPointIndex(currentIndex)
                    if (pointDistance > maxDistancePoint.distance) return {distance: pointDistance, index: currentIndex}
                    return maxDistancePoint
                },
                {distance: getDistanceForPointIndex(sectionStartIndex + 1), index: sectionStartIndex + 1},
            )
        }

        for (let iter = 0; iter < maxIters; iter++) {
            let countAdded = 0

            controlPoints.slice(0, -1).forEach((sectionStart, index) => {
                const sectionStartIndex = sectionStart.drawingPointIndex
                const sectionEndIndex = controlPoints[index + 1].drawingPointIndex
                const nextDrawingPointInfo = getMaxDistanceDrawingPointInSection(sectionStartIndex, sectionEndIndex)

                if (nextDrawingPointInfo === undefined || nextDrawingPointInfo.distance < threshold) return

                controlPoints.push({drawingPointIndex: nextDrawingPointInfo.index})
                countAdded += 1
            })

            controlPoints.sort((a, b) => a.drawingPointIndex - b.drawingPointIndex)

            if (countAdded === 0) break
        }

        return controlPoints
    }

    const getRefinedControlPoints = (controlPoints: ControlPoint[]) => {
        const drawingPathLength = initialDrawingPoints.reduce((acc, point, index) => acc + point.distance(initialDrawingPoints[index > 0 ? index - 1 : 0]), 0)

        const threshold = currentScale / 10

        const getCurvePointToDrawingPointIndexedDistance = (curvePointT: number, curve: ParametricCurve) => {
            const curvePointPosition = curve.getPoint(curvePointT)

            const controlPointsT = controlPoints.map((_, index) => index / (controlPoints.length - 1))
            const nextControlPointDrawingPointIndexIndex = controlPointsT.findIndex((t) => t > curvePointT)
            if (nextControlPointDrawingPointIndexIndex === -1) return undefined
            const nextControlPointT = controlPointsT[nextControlPointDrawingPointIndexIndex]
            const nextControlPointDrawingPointIndex = controlPoints[nextControlPointDrawingPointIndexIndex].drawingPointIndex
            if (nextControlPointDrawingPointIndex === 0) return undefined
            const previousControlPointT = controlPointsT[nextControlPointDrawingPointIndexIndex - 1]
            const previousControlPointDrawingPointIndex = controlPoints[nextControlPointDrawingPointIndexIndex - 1].drawingPointIndex

            const curvePointDrawingPointIndex = Math.floor(
                previousControlPointDrawingPointIndex +
                    ((nextControlPointDrawingPointIndex - previousControlPointDrawingPointIndex) * (curvePointT - previousControlPointT)) /
                        (nextControlPointT - previousControlPointT),
            )
            const curvePointDrawingPoint = initialDrawingPoints[curvePointDrawingPointIndex]

            const distance = new Vector2(curvePointPosition.x, curvePointPosition.y).distance(curvePointDrawingPoint)
            return {distance, index: curvePointDrawingPointIndex}
        }

        const getMaxCurvePointToDrawingPointIndexedDistance = (curve: ParametricCurve, samplesCount: number) => {
            let maxCurvePointToDrawingPointIndexedDistance: {distance: number; index: number} | undefined = undefined

            for (let i = 0; i <= samplesCount; i++) {
                const t = i / samplesCount
                const distance = getCurvePointToDrawingPointIndexedDistance(t, curve)
                if (!distance) continue

                if (!maxCurvePointToDrawingPointIndexedDistance || distance.distance > maxCurvePointToDrawingPointIndexedDistance.distance) {
                    maxCurvePointToDrawingPointIndexedDistance = distance
                }
            }

            return maxCurvePointToDrawingPointIndexedDistance
        }

        const maxIters = Math.max(Math.floor(drawingPathLength) * 10, 100)

        for (let iter = 0; iter < maxIters; iter++) {
            const parametricCurveControlPoints = controlPoints.map(
                (point) => new THREE.Vector3(initialDrawingPoints[point.drawingPointIndex].x, initialDrawingPoints[point.drawingPointIndex].y, 0),
            )
            const curve = new ParametricCurve(parametricCurveControlPoints, false, "centripetal", 1)

            const curveLength = curve.getLength()
            const samplesCount = Math.floor(curveLength < 1 ? 100 : curveLength * 100)

            const maxCurvePointToDrawingPointIndexedDistance = getMaxCurvePointToDrawingPointIndexedDistance(curve, samplesCount)

            if (
                !maxCurvePointToDrawingPointIndexedDistance ||
                maxCurvePointToDrawingPointIndexedDistance.distance < threshold ||
                controlPoints.find((point) => point.drawingPointIndex === maxCurvePointToDrawingPointIndexedDistance.index)
            )
                break

            const newControlPoint = {drawingPointIndex: maxCurvePointToDrawingPointIndexedDistance.index}
            controlPoints.push(newControlPoint)
            controlPoints = controlPoints.sort((a, b) => a.drawingPointIndex - b.drawingPointIndex)
        }

        return controlPoints
    }

    let controlPoints = getInitalControlPoints()
    controlPoints = getRefinedControlPoints(controlPoints)

    return controlPoints.map((point) => initialDrawingPoints[point.drawingPointIndex])
}

type CurvePoint = {
    position: Vector2Like
    segment: number
    sectionId: number
}

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: "cm-planar-curve-inspector",
    templateUrl: "./planar-curve-inspector.component.html",
    styleUrls: ["./planar-curve-inspector.component.scss"],
    imports: [InspectorSectionComponent, ValueSlotComponent, ButtonComponent, SplineCurveComponent],
    standalone: true,
})
export class PlanarCurveInspector extends BaseInspectorComponent<PlanarCurve> implements AfterViewInit, OnDestroy {
    readonly $booleanSchema = signal(z.boolean())

    readonly $backgroundElement = viewChild.required<ElementRef<SVGElement>>("background")

    readonly $isPanning = signal(false)
    readonly $panningStart = signal<Vector2 | undefined>(undefined)
    readonly $mode = signal<"default" | "drawing">("default")
    readonly $isDrawing = signal(false)

    readonly $scaleCm = signal(2)
    readonly $offsetCm = signal(new Vector2(0, 0))
    readonly $widthPx = signal<number>(270)
    readonly $heightPx = computed(() => this.$widthPx())

    private readonly _hostRef = inject(ElementRef<HTMLElement>)
    private _resizeObserver: ResizeObserver | undefined

    readonly $controlPoints = computed(() => this.$parameters().controlPoints)
    readonly $selectedControlPoint = model<number | undefined>(undefined, {alias: "selectedControlPoint"})
    readonly $hoveredControlPoint = model<number | undefined>(undefined, {alias: "hoveredControlPoint"})

    readonly $curveData = computed(() => {
        const node = this.$node()
        const infoNodeParts = this.sceneManagerService.getInfoNodeParts({templateNode: node, part: "root"})
        const infoNode = infoNodeParts.map((x) => x.infoNode).find((infoNode) => InfoNodes.PlanarCurve.is(infoNode))
        return infoNode?.curveData
    })

    readonly $splineControlPoints = computed(() => {
        const curveData = this.$curveData()
        if (!curveData) return []
        const scale = this.$scaleCm()
        const offset = this.$offsetCm()
        return curveData.controlPoints.map((point) => toSplineCoordinates(point.position, scale, offset))
    })

    readonly $curvePoints = computed<CurvePoint[]>(() => {
        const curveData = this.$curveData()
        if (!curveData || !curveData.completeCurveData.reified) return []

        const evaluatedCurvePoints = curveData.completeCurveData.reified.points
        const evaluatedCurveSegments = curveData.completeCurveData.reified.segments
        const evaluatedCurveSectionIds = curveData.completeCurveData.reified.sectionIds

        const curvePoints: CurvePoint[] = []
        for (let i = 0; i < evaluatedCurveSegments.length; i += 1) {
            curvePoints.push({
                position: {x: evaluatedCurvePoints[i * 3], y: evaluatedCurvePoints[i * 3 + 1]},
                segment: evaluatedCurveSegments[i],
                sectionId: evaluatedCurveSectionIds[i],
            })
        }
        return curvePoints
    })
    readonly curvePoints$ = toObservable(this.$curvePoints)
    readonly $splineCurvePoints = computed(() => {
        const scale = this.$scaleCm()
        const offset = this.$offsetCm()
        const curvePoints = this.$curvePoints()
        const controlPoints = this.$controlPoints()
        const closed = this.$parameters().closed

        const sectionIdForControlPointIdxMap = getSectionIdForControlPointIdxMap(controlPoints, closed)
        const sectionsCount = new Set(Object.values(sectionIdForControlPointIdxMap)).size

        return curvePoints.map((point) => ({
            position: toSplineCoordinates(point.position, scale, offset),
            color: colorForSectionId(point.sectionId, sectionsCount),
        }))
    })

    private readonly $drawingPoints = signal<Vector2[]>([])
    readonly $splineDrawingPoints = computed(() => {
        const scale = this.$scaleCm()
        const offset = this.$offsetCm()
        return this.$drawingPoints().map((point) => ({position: toSplineCoordinates(point, scale, offset), color: "blue"}))
    })
    readonly $drawingSmoothingFactor = signal(0.75)

    readonly $otherSplinePathsPoints = computed(() => {
        const splineDrawingPoints = this.$splineDrawingPoints()
        return [splineDrawingPoints]
    })

    private readonly $isDraggingControlPoint = signal(false)
    private readonly $$isModfifyingControlPoint = signal(false)

    constructor() {
        super()

        effect(() => {
            this.updateBackground()
        })
    }

    override ngOnInit() {
        super.ngOnInit()
        this.initScaleAndOffset()
    }

    ngAfterViewInit() {
        this._resizeObserver = new ResizeObserver((entries) => {
            const width = entries[0]?.contentRect.width
            if (width) this.$widthPx.set(Math.floor(width))
        })
        this._resizeObserver.observe(this._hostRef.nativeElement)
    }

    ngOnDestroy() {
        this._resizeObserver?.disconnect()
    }

    private initScaleAndOffset() {
        const controlPoints = this.$controlPoints()
        if (controlPoints.length === 0) return

        const _initScaleAndOffset = (curvePoints: CurvePoint[]) => {
            const boundingBox = curvePoints.reduce(
                ({min, max}, point) => {
                    const position = point.position

                    min.x = Math.min(min.x, position.x)
                    min.y = Math.min(min.y, position.y)
                    max.x = Math.max(max.x, position.x)
                    max.y = Math.max(max.y, position.y)

                    return {min, max}
                },
                {min: {x: 0, y: 0}, max: {x: 0, y: 0}},
            )

            const scale = Math.max(boundingBox.max.x - boundingBox.min.x, boundingBox.max.y - boundingBox.min.y) * 1.5
            const offset = new Vector2(boundingBox.max.x + boundingBox.min.x, boundingBox.max.y + boundingBox.min.y).mul(0.5)
            this.$scaleCm.set(scale)
            this.$offsetCm.set(offset)
        }

        const curvePoints = this.$curvePoints()
        if (curvePoints.length !== 0) {
            _initScaleAndOffset(curvePoints)
            return
        }

        this.curvePoints$.pipe(take(1)).subscribe((curvePoints) => _initScaleAndOffset(curvePoints))
    }

    updateBackground() {
        const widthPx = this.$widthPx()
        const heightPx = this.$heightPx()
        const scaleCm = this.$scaleCm()
        const offsetCm = this.$offsetCm()

        const backgroundElement = this.$backgroundElement()
        const svgElement = backgroundElement.nativeElement

        while (svgElement.firstChild) {
            svgElement.removeChild(svgElement.firstChild)
        }

        const toPixelCoordinates = (pointCm: Vector2) => splineToPixelCoordinates(toSplineCoordinates(pointCm, scaleCm, offsetCm), widthPx, heightPx)

        const setupLine = (startCm: Vector2, endCm: Vector2, width: number, color: string) => {
            const startPx = toPixelCoordinates(startCm)
            const endPx = toPixelCoordinates(endCm)
            const line = document.createElementNS("http://www.w3.org/2000/svg", "line")
            line.setAttribute("x1", `${startPx.x}`)
            line.setAttribute("y1", `${startPx.y}`)
            line.setAttribute("x2", `${endPx.x}`)
            line.setAttribute("y2", `${endPx.y}`)
            line.setAttribute("stroke", `${color}`)
            line.setAttribute("stroke-width", `${width}`)

            svgElement.appendChild(line)
        }

        const gridColor = "#d4d4d4"
        const axesColor = "black"

        const gridLinesWidth = 1.0
        const axesLinesWidth = 1.0

        const setupGrid = (offsetCm: Vector2, stepXCm: number, stepYCm: number) => {
            const startX = Math.floor((offsetCm.x - scaleCm) / stepXCm) * stepXCm
            const endX = Math.ceil((offsetCm.x + scaleCm) / stepXCm) * stepXCm
            const startY = Math.floor((offsetCm.y - scaleCm) / stepYCm) * stepYCm
            const endY = Math.ceil((offsetCm.y + scaleCm) / stepYCm) * stepYCm

            for (let x = startX; x <= endX; x += stepXCm) {
                setupLine(new Vector2(x, startY), new Vector2(x, endY), gridLinesWidth, gridColor)
            }
            for (let y = startY; y <= endY; y += stepYCm) {
                setupLine(new Vector2(startX, y), new Vector2(endX, y), gridLinesWidth, gridColor)
            }
        }

        const setupAxes = (offsetCm: Vector2) => {
            setupLine(new Vector2(offsetCm.x - scaleCm, 0), new Vector2(offsetCm.x + scaleCm, 0), axesLinesWidth, axesColor)
            setupLine(new Vector2(0, offsetCm.y - scaleCm), new Vector2(0, offsetCm.y + scaleCm), axesLinesWidth, axesColor)
        }

        const setupScaleLabel = (gridStepCm: number, width: number) => {
            const gridStepPx = Math.floor((gridStepCm / scaleCm) * widthPx)
            const startXPx = Math.floor(0.05 * widthPx)
            const endXPx = startXPx + gridStepPx
            const endYPx = Math.floor(0.95 * heightPx)
            const startYPx = endYPx

            const line = document.createElementNS("http://www.w3.org/2000/svg", "line")
            line.setAttribute("x1", `${startXPx}`)
            line.setAttribute("y1", `${startYPx}`)
            line.setAttribute("x2", `${endXPx}`)
            line.setAttribute("y2", `${endYPx}`)
            line.setAttribute("stroke", "black")
            line.setAttribute("stroke-width", `${width}`)
            svgElement.appendChild(line)

            const text = document.createElementNS("http://www.w3.org/2000/svg", "text")
            text.setAttribute("x", `${startXPx + 1}`)
            text.setAttribute("y", `${endYPx - 7}`)
            text.setAttribute("font-size", "12")
            text.setAttribute("fill", "black")
            text.setAttribute("text-anchor", "start")
            text.setAttribute("dominant-baseline", "middle")
            text.textContent = `${gridStepCm} cm`
            svgElement.appendChild(text)
        }

        const gridStep = (() => {
            const logScale = Math.floor(Math.log10(scaleCm * 2))
            const floorLogScale = Math.floor(logScale)
            return Math.pow(10, floorLogScale - 1)
        })()

        setupGrid(offsetCm, gridStep, gridStep)
        setupAxes(offsetCm)
        setupScaleLabel(gridStep, 3)
    }

    private $clickEvent = new Subject<{point: Vector2Like; type: "background" | "curve"}>()
    addControlPoint() {
        if (this.$clickEvent.observed) return

        this.$clickEvent.pipe(take(1)).subscribe(({point, type}) => {
            const scale = this.$scaleCm()
            const offset = this.$offsetCm()

            const newControlPointPosition = fromSplineCoordinates(point, scale, offset)
            const controlPoints = [...this.$parameters().controlPoints]
            const curvePoints = this.$curvePoints()

            const insertionIndex = (() => {
                if (type === "background") {
                    return undefined
                } else {
                    const minDistanceIndex = curvePoints.reduce((minIndex, currentPoint, currentIndex) => {
                        const distance = newControlPointPosition.distance(currentPoint.position)
                        return distance < newControlPointPosition.distance(curvePoints[minIndex].position) ? currentIndex : minIndex
                    }, 0)
                    return Math.floor(curvePoints[minDistanceIndex].segment) + 1
                }
            })()

            const newControlPoint: PlanarCurveControlPoint = {
                position: newControlPointPosition.toArray(),
                corner: false,
                sectionBreak: false,
            }

            if (insertionIndex !== undefined) {
                controlPoints.splice(insertionIndex, 0, newControlPoint)
            } else {
                controlPoints.push(newControlPoint)
            }

            this.sceneManagerService.modifyTemplateGraph(() => {
                this.$node().updateParameters({
                    controlPoints,
                })
            })
        })
    }

    handleBackgroundClicked(point: Vector2Like) {
        this.$clickEvent.next({point, type: "background"})
    }

    handleSplineClicked(point: Vector2Like) {
        this.$clickEvent.next({point, type: "curve"})
    }

    deleteControlPoint() {
        const selectedControlPointIdx = this.$selectedControlPoint()
        if (selectedControlPointIdx === undefined) return

        this.sceneManagerService.modifyTemplateGraph(() => {
            this.$node().updateParameters({
                controlPoints: this.$controlPoints().filter((_, index) => index !== selectedControlPointIdx),
            })
        })
    }

    deleteAllControlPoints() {
        this.sceneManagerService.modifyTemplateGraph(() => {
            this.$node().updateParameters({
                controlPoints: [],
            })
        })
    }

    beginControlPointDrag() {
        this.$isDraggingControlPoint.set(true)
    }

    endControlPointDrag() {
        if (this.$$isModfifyingControlPoint()) {
            this.sceneManagerService.endModifyTemplateGraph()
            this.$$isModfifyingControlPoint.set(false)
        }
        this.$isDraggingControlPoint.set(false)
    }

    changeControlPoint(pointId: number, point: Vector2Like) {
        if (!this.$isDraggingControlPoint()) return

        if (!this.$$isModfifyingControlPoint()) {
            this.sceneManagerService.beginModifyTemplateGraph()
            this.$$isModfifyingControlPoint.set(true)
        }

        const controlPoint = this.$controlPoints().find((p, i) => i === pointId)
        if (!controlPoint) return

        this.sceneManagerService.modifyTemplateGraph(() => {
            const scale = this.$scaleCm()
            const offset = this.$offsetCm()
            const newControlPoint: PlanarCurveControlPoint = {
                ...controlPoint,
                position: fromSplineCoordinates(point, scale, offset).toArray(),
            }

            this.$node().updateParameters({
                controlPoints: this.$controlPoints().map((p, i) => (i === pointId ? newControlPoint : p)),
            })
            this.onUpdatedParameters()
        })
    }

    handleBackgroundMouseDown() {
        const mode = this.$mode()
        if (mode === "default") {
            this.$isPanning.set(true)
        } else {
            this.$isDrawing.set(true)
            this.$drawingPoints.set([])
        }
    }

    handleBackgroundMouseUp() {
        const mode = this.$mode()
        if (mode === "default") {
            this.$isPanning.set(false)
            this.$panningStart.set(undefined)
        } else {
            this.$isDrawing.set(false)
            const drawingPoints = this.$drawingPoints()

            if (drawingPoints.length < 4) {
                this.$drawingPoints.set([])
                return
            }

            const controlPointsPositions = controlPointsFromDrawingPoints(drawingPoints, this.$scaleCm())

            const newControlPoints = controlPointsPositions.map((point) => {
                return {
                    position: point.toArray(),
                    normal: [1, 0] as [number, number],
                    corner: false,
                    sectionBreak: false,
                }
            })

            this.sceneManagerService.modifyTemplateGraph(() => {
                const controlPoints = this.$controlPoints()

                this.$node().updateParameters({
                    controlPoints: [...controlPoints, ...newControlPoints],
                })
            })

            this.$drawingPoints.set([])
        }
    }

    handleMouseMoved(change: Vector2Like) {
        const mode = this.$mode()

        if (mode === "default") {
            if (!this.$isPanning()) return

            const panningOffset = fromSplineCoordinates(change, this.$scaleCm(), this.$offsetCm())

            const panningStart = this.$panningStart()
            if (!panningStart) {
                this.$panningStart.set(panningOffset)
                return
            }

            const newOffset = this.$offsetCm().sub(panningOffset.sub(panningStart))
            this.$offsetCm.set(newOffset)
        } else {
            if (!this.$isDrawing()) return

            const newDrawingPoint = fromSplineCoordinates(change, this.$scaleCm(), this.$offsetCm())
            const currentDrawnPoints = this.$drawingPoints()
            const smoothingFactor = this.$drawingSmoothingFactor()
            const smoothedPoint = smoothPathPoint(newDrawingPoint, currentDrawnPoints, smoothingFactor)

            this.$drawingPoints.set([...currentDrawnPoints, smoothedPoint])
        }
    }

    handleZoomChanged(event: {deltaX: number; deltaY: number}) {
        const scale = this.$scaleCm()
        const newScale = Math.max(0.1, scale * (1.0 + event.deltaY * 0.001))
        this.$scaleCm.set(newScale)
    }

    toggleDrawingMode() {
        const mode = this.$mode()
        this.$mode.set(mode === "default" ? "drawing" : "default")
    }

    flipControlPoints() {
        const controlPoints = this.$controlPoints()
        const reversedControlPoints = [...controlPoints].reverse()

        this.sceneManagerService.modifyTemplateGraph(() => {
            this.$node().updateParameters({
                controlPoints: reversedControlPoints,
            })
        })
    }

    rotateControlPoints(angle: number) {
        const controlPoints = this.$controlPoints()
        const rotatedControlPointsPositions = rotatePoints(
            controlPoints.map((point) => new Vector2(point.position[0], point.position[1])),
            angle,
        )

        const rotatedControlPoints = rotatedControlPointsPositions.map((point, index) => {
            const originalControlPoint = controlPoints[index]
            return {
                ...originalControlPoint,
                position: point.toArray(),
            }
        })

        this.sceneManagerService.modifyTemplateGraph(() => {
            this.$node().updateParameters({
                controlPoints: rotatedControlPoints,
            })
        })
    }
}
